import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SaveAsset, uploadAttachment, getAttachments } from "./AssetService";
import { GetTypeCatalog } from "./TypeCatalogService";
import { formatDateForMySQL } from "../../../components/formatDate";
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useInventoryPermission } from "../permissions/InventoryPermissions";
import { GetJobsite } from "screens/Customers/logic/GetJobsites";

export const useAddAssetLogic = ({ handleClose, setLoading, open, selectedAsset }) => {
    //#region Permissions
    const { InventoryPermissionProps } = useInventoryPermission();
    const {
      // Asset Permissions
      hasAssetSavePermission,
  
      // Asset & Material Type Permissions
      hasTypeCatalogGetPermission,
    } = InventoryPermissionProps;
    //#endregion Permissions

  // Form state
  const [assetForm, setAssetForm] = useState({
    assetID: 0,
    assetName: "",
    assetTypeID: "",
    licensePlate: "",
    serialVINNumber: "",
    description: "",
    hourlyRate: "",
    designatedOperator: "",
    operationalCostsAmount: "",
    operationalCostsUnit: "",
    nextMaintenanceDate: null,
    lastMaintenanceDate: null,
    purchaseDate: null,
    purchasePrice: "",
    warrantyExpiration: null,
    gpsTracking: false,
    status: 'Available',
    assetLocation: null
  });

  // Asset types for dropdown
  const navigate = useNavigate();
  const [assetTypes, setAssetTypes] = useState([]);
  const [jobsites, setJobsites] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentAttachment, setCurrentAttachment] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState('')

  // Date constraints
  const today = dayjs();

  const isEditMode = Boolean(selectedAsset && selectedAsset.assetID);

  // Fetch asset types on component mount or when dialog opens
  // useEffect(() => {
  //   if (open) {
  //     fetchAssetTypes();
  //   }
  // }, [open]);
  useEffect(() => {
    if (open) {
      // Fetch all asset types via API (using typeCatalogID=0 to return all asset types)
      async function fetchAssetTypes() {
        if(!hasTypeCatalogGetPermission) return;

        const typesResponse = await GetTypeCatalog("Asset");
        if (!typesResponse.isError && typesResponse.result) {
          setAssetTypes(typesResponse.result);
        } else {
          // Handle errors as desired (for example, show a toast)
          console.error("Failed to load asset types:", typesResponse.message);
        }
      }
      if(hasTypeCatalogGetPermission){
        fetchAssetTypes();
      }

      if (selectedAsset) {
        // When editing, prepopulate the asset form.
        // Convert assetTypeID to a number (if your API returns it as such)
        const assetTypeId = selectedAsset.assetTypeID ? Number(selectedAsset.assetTypeID) : "";
        setAssetForm({
          ...selectedAsset,
          assetTypeID: assetTypeId, // ensure data type match to MenuItem values
          // Convert any date strings from the backend to dayjs objects if needed:
          nextMaintenanceDate: selectedAsset.nextMaintenanceDate ? dayjs(selectedAsset.nextMaintenanceDate) : null,
          lastMaintenanceDate: selectedAsset.lastMaintenanceDate ? dayjs(selectedAsset.lastMaintenanceDate) : null,
          purchaseDate: selectedAsset.purchaseDate ? dayjs(selectedAsset.purchaseDate) : null,
          warrantyExpiration: selectedAsset.warrantyExpiration ? dayjs(selectedAsset.warrantyExpiration) : null,
        });
        
        // Fetch existing attachment if in edit mode
        if (selectedAsset.assetID) {
          fetchAssetAttachment(selectedAsset.assetID);
        }
      }
    }
  }, [open, selectedAsset, hasTypeCatalogGetPermission]);

  useEffect(() => {
    const fetchJobsites = async () => {
      try{
        const result = await GetJobsite(0, 0, navigate)
        if(result){
          setJobsites(result)
        }
      }catch(error){
        console.error('Error fetching jobsites', error)
      }
    }

    fetchJobsites();
  }, [])

  // Fetch asset attachment
  const fetchAssetAttachment = async (assetID) => {
    try {
      setLoading(true);
      const response = await getAttachments(assetID);
      if (!response.isError && Array.isArray(response.result) && response.result.length) {
        const attachment = response.result[0];
        // store for debugging if you like
        setCurrentAttachment(attachment);
        // seed selectedFile so the UI shows the “paperclip” + name
        setSelectedFile({
          isExisting: true,               // flag so we don’t re-upload
          name: attachment.fileName,      // used in your Tooltip
          url: attachment.fileUrl,        // if you want to preview or download
          mediaID: attachment.mediaID,    // in case you need to delete it later
        });
      }
    } catch (error) {
      console.error("Error fetching asset attachment:", error);
    } finally {
      setLoading(false);
    }
  };

  // Log when currentAttachment is updated
  // useEffect(() => {
    // if (currentAttachment) {
    //   console.log('Current attachment updated to:', currentAttachment);
    // }
  // }, [currentAttachment]);

  const fetchAssetTypes = async () => {
    try {
      setLoading(true);
      const response = await GetTypeCatalog("asset");
      if (!response.isError && response.result) {
        // Add a small timestamp to force React to recognize the change
        const assetTypesWithTimestamp = response.result.map(type => ({
          ...type,
          _timestamp: Date.now()
        }));
        setAssetTypes(assetTypesWithTimestamp);
      } else {
        console.error("Error fetching asset types:", response.message);
    //     toast.error("Failed to load asset types", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
      }
    } catch (error) {
      console.error("Error fetching asset types:", error);
    //   toast.error("Failed to load asset types", {
    //   style: {
    //     fontFamily: '"DM Sans", sans-serif',
    //     fontSize: '15px',
    //   },
    // });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('assetForm', assetForm)
  }, [assetForm])

  const handleChangeAssetLocation = (jobsiteID) => {
    setAssetForm({
      ...assetForm,
      assetLocation: jobsiteID
    });
  }

  // Form change handlers
  const handleInputChange = (e) => {
    if(!hasAssetSavePermission) return;

    const assetType = assetTypes.find(item => item.typeCatalogID === e.target.value)

    if (assetType) {
      setSelectedAssetType(assetType.typeName.toLowerCase())
    }

    const { name, value } = e.target;
    setAssetForm({
      ...assetForm,
      [name]: value
    });
    
    // Clear error for this field if any
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
const removeCommas = (value) => value.replace(/,/g, '');
const handleNumberInputChange = (e) => {
  if (!hasAssetSavePermission) return;

  const { name, value } = e.target;

  // Allow empty input
  if (value === "") {
    setAssetForm({
      ...assetForm,
      [name]: ""
    });
    return;
  }

  // Remove commas for validation
  const rawValue = value.replace(/,/g, "");

  // Regex: allow digits and optional decimal (including starting with a dot)
  const validNumberRegex = /^(\d*\.?\d*)$/;

  if (validNumberRegex.test(rawValue)) {
    setAssetForm({
      ...assetForm,
      [name]: rawValue // store plain value while typing
    });

    // Clear error if any
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  }
};


  const handleDateChange = (name, value) => {
    if(!hasAssetSavePermission) return;

    // Validate date based on constraints
    let dateError = null;

    // only enforce your “future / past” rules when NOT editing
    if (!isEditMode && value) {
      // Next Maintenance Date: must be future
      if (name === 'nextMaintenanceDate' && value.isBefore(today, 'day')) {
        dateError = 'Next maintenance date must be in the future';
      }
          
    }

    if(value){

      // Purchase Date: must be past or today
      if (name === 'purchaseDate' && value.isAfter(today, 'day')) {
        dateError = 'Purchase date cannot be in the future';
      }  

      // Last Maintenance Date: must be past or today
      if (name === 'lastMaintenanceDate' && value.isAfter(today, 'day')) {
        dateError = 'Last maintenance date cannot be in the future';
      }
    
      // Cross-field: lastMaintenanceDate must be after purchaseDate
    if (name === 'lastMaintenanceDate' && assetForm.purchaseDate 
      && value.isBefore(assetForm.purchaseDate, 'day')) {
      dateError = 'Last maintenance date must be after Purchase Date';
    }
    }

    setAssetForm({
      ...assetForm,
      [name]: value
    });
    
    // Update error for this field
    setErrors({
      ...errors,
      [name]: dateError
    });
  };

  // const handleCheckboxChange = (e) => {
  //   if(!hasAssetSavePermission) return;

  //   const { name, checked } = e.target;
  //   setAssetForm({
  //     ...assetForm,
  //     [name]: checked
  //   });
  // };

  const handleStatusChange = (e) => {
    if(!hasAssetSavePermission) return;

    const { value } = e.target;
    setAssetForm({
      ...assetForm,
      status: value
    });
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!assetForm.assetName.trim()) {
      newErrors.assetName = "Asset Name is required";
    }
    
    if (!assetForm.assetTypeID) {
      newErrors.assetTypeID = "Asset Type is required";
    }

    if (!isEditMode) {
    // Date validation
    if (assetForm.nextMaintenanceDate && assetForm.nextMaintenanceDate.isBefore(today, 'day')) {
      newErrors.nextMaintenanceDate = 'Next maintenance date must be in the future';
    }

  }
  
    if (assetForm.lastMaintenanceDate && assetForm.lastMaintenanceDate.isAfter(today, 'day')) {
      newErrors.lastMaintenanceDate = 'Last maintenance date cannot be in the future';
    }
    
    if (assetForm.purchaseDate && assetForm.purchaseDate.isAfter(today, 'day')) {
      newErrors.purchaseDate = 'Purchase date cannot be in the future';
    }
    // validate again on save that lastMaintenanceDate > purchaseDate
    if (
      assetForm.lastMaintenanceDate
      && assetForm.purchaseDate
      && assetForm.lastMaintenanceDate.isBefore(assetForm.purchaseDate, 'day')
    ) {
      newErrors.lastMaintenanceDate = 'Last maintenance date must be after Purchase Date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

// Save asset
const handleSaveAsset = async () => {
  if(!hasAssetSavePermission) return;

  try {
    if (!validateForm()) {
      return false;
    }

    setLoading(true);

    // Format dates for API
    const formattedAsset = {
      ...assetForm,
      hourlyRate: assetForm.hourlyRate ? Number(assetForm.hourlyRate) : 0,
      operationalCostsAmount: assetForm.operationalCostsAmount ? Number(assetForm.operationalCostsAmount) : 0,
      purchasePrice: assetForm.purchasePrice ? Number(assetForm.purchasePrice) : 0,
      nextMaintenanceDate: assetForm.nextMaintenanceDate ? formatDateForMySQL(assetForm.nextMaintenanceDate.toDate()) : null,
      lastMaintenanceDate: assetForm.lastMaintenanceDate ? formatDateForMySQL(assetForm.lastMaintenanceDate.toDate()) : null,
      purchaseDate: assetForm.purchaseDate ? formatDateForMySQL(assetForm.purchaseDate.toDate()) : null,
      warrantyExpiration: assetForm.warrantyExpiration ? formatDateForMySQL(assetForm.warrantyExpiration.toDate()) : null,
      
    };

    const response = await SaveAsset(formattedAsset);
    // console.log(response);

    if (!response.isError) {
      // If we have a file to upload and the asset was created successfully
      if (selectedFile && !selectedFile.isExisting && response.result?.assetID) {
        const uploadResponse = await uploadAttachment(response.result.assetID, selectedFile);
        if (uploadResponse.isError) {
          toast.error(uploadResponse.message || "Failed to upload attachment");
        } else {
          toast.success("Asset and attachment created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        }
      } else {
        toast.success(assetForm.assetID ? "Asset updated successfully" : "Asset created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
      handleClose();
      return true;
    } else {
      toast.error(response.message || "Failed to save asset", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      console.error("Error saving asset:", response.message);
      return false;
    }
  } catch (error) {
    toast.error("An error occurred while saving the asset", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    console.error("Error saving asset:", error);
    return false;
  } finally {
    setLoading(false);
  }
};


  const handleFileChange = (file) => {
    if(!hasAssetSavePermission) return;

    setSelectedFile(file);
  };

  const resetForm = () => {
    if(!hasAssetSavePermission) return;

    setAssetForm({
      assetID: 0,
      assetName: "",
      assetTypeID: "",
      licensePlate: "",
      serialVINNumber: "",
      description: "",
      hourlyRate: "",
      designatedOperator: "",
      operationalCostsAmount: "",
      operationalCostsUnit: "",
      nextMaintenanceDate: null,
      lastMaintenanceDate: null,
      purchaseDate: null,
      purchasePrice: "",
      warrantyExpiration: null,
      gpsTracking: false,
      status: '',
      assetLocation: null
    });
    setSelectedFile(null);
    setErrors({});
    setSelectedAssetType('');
  };

  return {
    assetForm,
    assetTypes,
    selectedAssetType,
    errors,
    today,
    handleInputChange,
    handleNumberInputChange,
    handleDateChange,
    // handleCheckboxChange,
    handleStatusChange,
    handleSaveAsset,
    resetForm,
    handleFileChange,
    selectedFile,
    currentAttachment,
    setSelectedFile,
    jobsites,
    handleChangeAssetLocation
  };
}; 